<template>
	<div class="region-selector">
		<!-- 三级联动布局 -->
		<div class="region-wrapper">
			<!-- 第一级 -->
			<div class="region-level">
				<div v-for="(item, index) in level1" :key="index" class="level-item" :class="{ active: activeLevel1 === index }"
					@click="switchLevel1(index)">
					{{ item.name }}
					<span class="tip-num" v-if="levelSelectedMap[item.id]">({{ levelSelectedMap[item.id] }})</span>
				</div>
			</div>

			<!-- 第二级 -->
			<div class="region-level level2">
				<div v-for="(item, index) in level2" :key="index" class="level-item" :class="{ active: activeLevel2 === index }"
					@click="switchLevel2(index)">
					{{ item.name }}
					<span class="tip-num" v-if="levelSelectedMap[item.id]">({{ levelSelectedMap[item.id] }})</span>
				</div>
			</div>

			<!-- 第三级（可多选 + 顶部全选） -->
			<div class="region-level level3">
				<div class="level-item all-select" @click="selectAllLevel3">
					<div class="checkmark" :class="{ active: isAllSelected }"></div>
					全选
				</div>
				<div v-for="(item, index) in level3" :key="index" class="level-item" @click="toggleLevel3(item)">
					<div class="checkmark" :class="{ active: selectedIds.includes(item.id) }"></div>
					{{ item.name }}
				</div>
			</div>
		</div>

		<!-- 底部按钮 -->
		<div class="region-footer">
			<!-- 已选数量 -->
			<div class="selected-count">
				已选 {{ selectedList.length }} 个
			</div>
			<div class="flex-center">
				<el-button @click="clearAll">清空已选</el-button>
				<el-button type="primary" @click="confirmSelected">确定</el-button>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: "OptionPosition",
		props: {
			list: {
				type: Array,
				required: true,
				default: () => []
			}
		},

		watch: {
			list: {
				handler(newVal) {
					if (newVal && newVal.length > 0) {
						this.initData()
					}
				},
				deep: true,
				immediate: true
			}
		},

		data() {
			return {
				level1: [], // 第一级列表
				level2: [], // 第二级列表
				level3: [], // 第三级列表

				activeLevel1: 0, // 第一级选中索引
				activeLevel2: 0, // 第二级选中索引
				selectedIds: [], // 第三级选中ID
				selectedList: [] // 第三级选中完整数据
			};
		},

		computed: {
			isAllSelected() {
				if (this.level3.length === 0) return false
				return this.level3.every(item => this.selectedIds.includes(item.id))
			},
			levelSelectedMap() {
				const map = {}
				const sid = new Set(this.selectedIds)

				this.level1.forEach(l1 => {
					let count = 0
					if (l1.children) {
						l1.children.forEach(l2 => {
							let c2 = 0
							if (l2.children) {
								l2.children.forEach(l3 => {
									if (sid.has(l3.id)) c2++
								})
							}
							map[l2.id] = c2
							count += c2
						})
					}
					map[l1.id] = count
				})
				return map
			}
		},
		created() {
			this.initData();
		},
		methods: {
			// 初始化数据
			initData() {
				this.level1 = this.list;
				if (this.level1.length > 0) {
					this.switchLevel1(0);
				}
			},

			// 切换第一级 → 加载第二级
			switchLevel1(index) {
				this.activeLevel1 = index;
				const item = this.level1[index];
				this.level2 = item.children || [];
				if (this.level2.length > 0) {
					this.switchLevel2(0);
				} else {
					this.level3 = [];
				}
			},

			// 切换第二级 → 加载第三级
			switchLevel2(index) {
				this.activeLevel2 = index;
				const item = this.level2[index];
				this.level3 = item.children || [];
			},

			// 第三级 单选/取消
			toggleLevel3(item) {
				const index = this.selectedIds.indexOf(item.id);
				if (index > -1) {
					this.selectedIds.splice(index, 1);
					this.selectedList = this.selectedList.filter(i => i.id !== item.id);
				} else {
					this.selectedIds.push(item.id);
					this.selectedList.push(item);
				}
			},

			// 第三级 当前组 全选
			selectAllLevel3() {
				if (this.isAllSelected) {
					this.selectedIds = []
					this.selectedList = []
				} else {
					const ids = this.level3.map(i => i.id);
					const items = [...this.level3];
					this.selectedIds = ids;
					this.selectedList = items;
				}
			},

			/**
			 * 删除指定的选中项（供父组件调用）
			 * @param {Object} item - 要删除的选项对象 { id, name }
			 */
			deleteSelectedItem(item) {
				const index = this.selectedIds.indexOf(item.id);
				if (index > -1) {
					this.selectedIds.splice(index, 1);
					this.selectedList = this.selectedList.filter(i => i.id !== item.id);
				}
				// 触发确认事件，同步父组件状态
				this.confirmSelected()
			},

			// 清空已选，并通知父组件
			clearAll() {
				this.selectedIds = []
				this.selectedList = []
				// 通知父组件已清空
				this.$emit("clear")
			},

			// 确定 → 返回选中的第三级
			confirmSelected() {
				const selectedData = {
					totalCount: this.selectedList.length,
					groups: [{
						name: '位置',
						selectedItems: this.selectedList,
						selectedIds: this.selectedIds.join(',')
					}]
				}
				this.$emit("confirm", selectedData)
			}
		}
	};
</script>

<style scoped lang="scss">
	.region-selector {
		width: 700px;
		padding: 5px;
		background: #fff;
		border-radius: 8px;
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
	}

	.region-wrapper {
		display: flex;
		gap: 12px;
		margin-bottom: 15px;
		max-height: 400px;
		overflow-y: auto;
	}

	.region-level {
		flex: 1;
		display: flex;
		flex-direction: column;
		// gap: 6px;
		padding: 8px;
		// border: 1px solid #eee;
		border-radius: 6px;
		height: 300px;
		overflow-y: auto;

		.tip-num {
			font-size: 12px;
			color: #999;
		}
	}

	.level2 {
		border-left: 1px solid #eee;
		border-right: 1px solid #eee;
	}

	.level-item {
		padding: 8px 12px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
		transition: 0.2s;
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.level-item:hover {
		background: #f5f5f5;
	}

	.region-level:not(.level3) .level-item.active {
		background: #e6f0ff;
		color: #28b05e;
	}

	.checkmark {
		width: 16px;
		height: 16px;
		border: 1px solid #d9d9d9;
		border-radius: 2px;
		margin-right: 8px;
		display: flex;
		align-items: center;
		justify-content: center;

		&.active {
			background: #28b05e;
			border-color: #28b05e;

			&::after {
				content: '✔';
				color: #fff;
				font-size: 12px;
			}
		}
	}

	.all-select {
		// background: #e6f0ff;
		// color: #28b05e;
		font-weight: 500;
		margin-bottom: 4px;
	}

	.region-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 20px;
		border-top: 1px solid #f0f0f0;

		.el-button {
			border-radius: 8px;
			width: 90px;

			&--active {
				border: 1px solid #28b05e;
				color: #28b05e;
			}

			&--primary {
				border: 1px solid #28b05e;
				background-color: #28b05e;
				color: #fff;
			}
		}
	}

	.selected-count {
		color: #666;
		font-size: 14px;
	}
</style>
